/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import { TableViewDropdown as CarbonTableViewDropdown } from 'carbon-addons-iot-react';
import { useIntl, defineMessages } from 'react-intl';
import noop from 'lodash/noop';

import { DropdownItemType } from 'TableTypes';

const messages = defineMessages({
  view: 'View',
  edited: 'Edited',
  viewAll: 'View all',
  saveAsNewView: 'Save as new view',
  saveChanges: 'Save changes',
  manageViews: 'Manage views',
  ariaLabel: 'Select view',
  tableViewMenu: 'Table view menu',
  'close.menu': 'Close menu',
  'open.menu': 'Open menu',
  'clear.all': 'Clear all',
  'clear.selection': 'Clear selection',
});

type TableViewDropdownType = {
  selectedViewId: string;
  isSelectedViewEdited: boolean;
  views: DropdownItemType[];
  onSaveAsNewView: () => void;
  onManageViews: () => void;
  onChangeView: (item: DropdownItemType) => void;
  onSaveChanges: () => void;
};

const TableViewDropdown = ({
  selectedViewId,
  isSelectedViewEdited = false,
  views,
  onSaveAsNewView = noop,
  onManageViews = noop,
  onChangeView = noop,
  onSaveChanges = noop,
}: TableViewDropdownType): JSX.Element => {
  const { formatMessage } = useIntl();
  return (
    <CarbonTableViewDropdown
      style={{ order: '-1', width: '300px' }}
      selectedViewId={selectedViewId}
      selectedViewEdited={isSelectedViewEdited}
      views={views}
      actions={{
        onSaveAsNewView,
        onManageViews,
        onChangeView,
        onSaveChanges,
      }}
      overrides={{
        dropdown: {
          props: {
            translateWithId: id => formatMessage(messages[id]),
          },
        },
      }}
      i18n={{
        view: formatMessage(messages.view),
        edited: formatMessage(messages.edited),
        viewAll: formatMessage(messages.viewAll),
        saveAsNewView: formatMessage(messages.saveAsNewView),
        saveChanges: formatMessage(messages.saveChanges),
        manageViews: formatMessage(messages.manageViews),
        ariaLabel: formatMessage(messages.ariaLabel),
        tableViewMenu: formatMessage(messages.tableViewMenu),
      }}
      testId="table-view-dropdown"
    />
  );
};

export default TableViewDropdown;
